import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = process.env.REACT_APP_BACKEND_URL || '';

const BrandContext = createContext({
  brands: [],
  activeBrandId: null,
  activeBrand: null,
  loading: false,
  refresh: () => {},
  switchBrand: () => {},
  createBrand: () => {},
});

/**
 * BrandProvider — Phase 5 Multi-Brand Agency Tier.
 *
 * Lazily loads brands once per authenticated session. Skips entirely when no
 * user is logged in OR when the user is super_admin (super admins manage the
 * platform, they don't operate brands).
 *
 * Persists the user's chosen active brand via `POST /api/brands/active` so it
 * survives logouts and is the single source of truth across devices.
 */
export function BrandProvider({ children }) {
  const { user } = useAuth();
  const [brands, setBrands] = useState([]);
  const [activeBrandId, setActiveBrandId] = useState(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user || user.role === 'super_admin') return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/brands`);
      setBrands(data.brands || []);
      setActiveBrandId(data.active_brand_id || null);
    } catch (err) {
      // silent — multi-brand is progressive disclosure
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const switchBrand = useCallback(async (brandId) => {
    if (!brandId || brandId === activeBrandId) return;
    try {
      await axios.post(`${API}/api/brands/active`, { brand_id: brandId });
      setActiveBrandId(brandId);
      // Re-render the app — most pages read `useBrand()` and refetch on
      // active brand change via their own useEffect dependency array.
      window.dispatchEvent(new CustomEvent('konnect:brand-switched', { detail: { brandId } }));
    } catch (err) {
      // toast handled by caller
      throw err;
    }
  }, [activeBrandId]);

  const createBrand = useCallback(async ({ name, description, color }) => {
    const { data } = await axios.post(`${API}/api/brands`, { name, description, color });
    await refresh();
    return data;
  }, [refresh]);

  const activeBrand = brands.find(b => b.id === activeBrandId) || null;

  return (
    <BrandContext.Provider value={{
      brands, activeBrandId, activeBrand, loading, refresh, switchBrand, createBrand,
    }}>
      {children}
    </BrandContext.Provider>
  );
}

export const useBrand = () => useContext(BrandContext);
